import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { Trophy, Star, Zap, Lock, ChevronRight, Sparkles } from 'lucide-react';

// Nanouk Level Images
const NANOUK_LEVELS = {
    newcomer: {
        image: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
        minLevel: 1,
        maxLevel: 5,
        color: 'from-cyan-400 to-blue-500',
        bgColor: 'bg-cyan-500/10',
        borderColor: 'border-primary/30',
    },
    explorer: {
        image: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
        minLevel: 6,
        maxLevel: 15,
        color: 'from-orange-400 to-amber-500',
        bgColor: 'bg-orange-500/10',
        borderColor: 'border-orange-500/30',
    },
    trailblazer: {
        image: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
        minLevel: 16,
        maxLevel: 30,
        color: 'from-violet-400 to-purple-500',
        bgColor: 'bg-violet-500/10',
        borderColor: 'border-violet-500/30',
    },
    alpha: {
        image: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
        minLevel: 31,
        maxLevel: 999,
        color: 'from-pink-400 via-purple-400 to-cyan-400',
        bgColor: 'bg-gradient-to-br from-pink-500/10 via-purple-500/10 to-cyan-500/10',
        borderColor: 'border-purple-500/30',
    },
};

const content = {
    de: {
        title: 'Nanouk Level',
        subtitle: 'Dein BestBuddy wächst mit dir!',
        currentLevel: 'Aktuelles Level',
        xpToNext: 'XP bis zum nächsten Level',
        stages: {
            newcomer: { name: 'Newcomer', desc: 'Ein neugieriger Welpe mit Bandana, bereit die Welt zu entdecken.' },
            explorer: { name: 'Explorer', desc: 'Ein junger Abenteurer mit Rucksack, der neue Wege erkundet.' },
            trailblazer: { name: 'Trailblazer', desc: 'Ein athletischer Champion mit Smart-Weste und Peak-Fitness.' },
            alpha: { name: 'Alpha Buddy', desc: 'Der legendäre Anführer mit Nordlicht-Halsband. Maximale Stufe!' },
        },
        unlockAt: 'Freischaltung bei Level',
        currentStage: 'Aktuelle Stufe',
        viewAll: 'Alle Stufen ansehen',
        locked: 'Gesperrt',
        unlocked: 'Freigeschaltet',
    },
    en: {
        title: 'Nanouk Level',
        subtitle: 'Your BestBuddy grows with you!',
        currentLevel: 'Current Level',
        xpToNext: 'XP to next level',
        stages: {
            newcomer: { name: 'Newcomer', desc: 'A curious puppy with bandana, ready to explore the world.' },
            explorer: { name: 'Explorer', desc: 'A young adventurer with backpack, exploring new paths.' },
            trailblazer: { name: 'Trailblazer', desc: 'An athletic champion with smart vest and peak fitness.' },
            alpha: { name: 'Alpha Buddy', desc: 'The legendary leader with aurora collar. Maximum stage!' },
        },
        unlockAt: 'Unlocks at Level',
        currentStage: 'Current Stage',
        viewAll: 'View all stages',
        locked: 'Locked',
        unlocked: 'Unlocked',
    },
};

export function getNanoukStage(level) {
    if (level >= 31) return 'alpha';
    if (level >= 16) return 'trailblazer';
    if (level >= 6) return 'explorer';
    return 'newcomer';
}

export function getNanoukImage(level) {
    const stage = getNanoukStage(level);
    return NANOUK_LEVELS[stage].image;
}

export function NanoukLevelCard({ level = 1, xp = 0, totalXp = 100 }) {
    const { language } = useLanguage();
    const t = content[language] || content.de;
    const [showAllStages, setShowAllStages] = useState(false);
    
    const stage = getNanoukStage(level);
    const stageData = NANOUK_LEVELS[stage];
    const progress = Math.min((xp / totalXp) * 100, 100);
    
    const stages = ['newcomer', 'explorer', 'trailblazer', 'alpha'];

    return (
        <>
            <Card className={`overflow-hidden ${stageData.bgColor} ${stageData.borderColor} border-2`}>
                <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                        {/* Nanouk Image */}
                        <motion.div 
                            className="relative"
                            whileHover={{ scale: 1.05 }}
                            transition={{ type: 'spring', stiffness: 300 }}
                        >
                            <div className={`w-20 h-20 rounded-2xl overflow-hidden ring-4 ring-offset-2 ring-offset-background bg-gradient-to-br ${stageData.color} ring-white/20`}>
                                <img 
                                    src={stageData.image} 
                                    alt={`Nanouk ${t.stages[stage].name}`}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gradient-to-br ${stageData.color} flex items-center justify-center text-foreground font-bold text-sm shadow-lg`}>
                                {level}
                            </div>
                        </motion.div>

                        {/* Level Info */}
                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <Badge className={`bg-gradient-to-r ${stageData.color} text-foreground border-0`}>
                                        {t.stages[stage].name}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">
                                        Level {level}
                                    </span>
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-xs gap-1 h-7"
                                    onClick={() => setShowAllStages(true)}
                                >
                                    {t.viewAll}
                                    <ChevronRight className="w-3 h-3" />
                                </Button>
                            </div>
                            
                            <div className="space-y-2">
                                <div className="flex justify-between text-xs">
                                    <span className="text-muted-foreground">{t.xpToNext}</span>
                                    <span className="font-medium">{xp} / {totalXp} XP</span>
                                </div>
                                <Progress value={progress} className="h-2" />
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* All Stages Dialog */}
            <Dialog open={showAllStages} onOpenChange={setShowAllStages}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-yellow-500" />
                            {t.title}
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground mb-4">{t.subtitle}</p>
                    
                    <div className="space-y-4">
                        {stages.map((stageName, index) => {
                            const stageInfo = NANOUK_LEVELS[stageName];
                            const isUnlocked = level >= stageInfo.minLevel;
                            const isCurrent = stage === stageName;
                            
                            return (
                                <motion.div
                                    key={stageName}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className={`relative flex items-center gap-4 p-3 rounded-xl border-2 transition-all ${
                                        isCurrent 
                                            ? `${stageInfo.bgColor} ${stageInfo.borderColor}` 
                                            : isUnlocked 
                                                ? 'bg-secondary/50 border-border' 
                                                : 'bg-muted/30 border-muted opacity-60'
                                    }`}
                                >
                                    {/* Stage Image */}
                                    <div className={`relative w-16 h-16 rounded-xl overflow-hidden ${!isUnlocked ? 'grayscale' : ''}`}>
                                        <img 
                                            src={stageInfo.image} 
                                            alt={t.stages[stageName].name}
                                            className="w-full h-full object-cover"
                                        />
                                        {!isUnlocked && (
                                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                                <Lock className="w-5 h-5 text-foreground" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Stage Info */}
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-semibold">{t.stages[stageName].name}</span>
                                            {isCurrent && (
                                                <Badge variant="outline" className="text-xs gap-1">
                                                    <Sparkles className="w-3 h-3" />
                                                    {t.currentStage}
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground mb-1">
                                            {t.stages[stageName].desc}
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <Badge variant={isUnlocked ? 'default' : 'secondary'} className="text-xs">
                                                {isUnlocked ? (
                                                    <><Star className="w-3 h-3 mr-1" /> {t.unlocked}</>
                                                ) : (
                                                    <><Lock className="w-3 h-3 mr-1" /> {t.unlockAt} {stageInfo.minLevel}</>
                                                )}
                                            </Badge>
                                        </div>
                                    </div>

                                    {/* Level Range */}
                                    <div className="text-right">
                                        <div className={`text-lg font-bold bg-gradient-to-r ${stageInfo.color} bg-clip-text text-transparent`}>
                                            {stageInfo.minLevel}-{stageInfo.maxLevel === 999 ? '∞' : stageInfo.maxLevel}
                                        </div>
                                        <div className="text-xs text-muted-foreground">Level</div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

export function NanoukAvatar({ level = 1, size = 'md', showLevel = true, className = '' }) {
    const stage = getNanoukStage(level);
    const stageData = NANOUK_LEVELS[stage];
    
    const sizes = {
        sm: 'w-10 h-10',
        md: 'w-16 h-16',
        lg: 'w-24 h-24',
        xl: 'w-32 h-32',
    };

    return (
        <motion.div 
            className={`relative ${className}`}
            whileHover={{ scale: 1.05 }}
        >
            <div className={`${sizes[size]} rounded-2xl overflow-hidden ring-2 ring-offset-2 ring-offset-background bg-gradient-to-br ${stageData.color} ring-white/20`}>
                <img 
                    src={stageData.image} 
                    alt="Nanouk"
                    className="w-full h-full object-cover"
                />
            </div>
            {showLevel && (
                <div className={`absolute -bottom-1 -right-1 ${size === 'sm' ? 'w-5 h-5 text-xs' : 'w-7 h-7 text-sm'} rounded-full bg-gradient-to-br ${stageData.color} flex items-center justify-center text-foreground font-bold shadow-lg`}>
                    {level}
                </div>
            )}
        </motion.div>
    );
}

export { NANOUK_LEVELS };
